<!-- Copyright (C) 2025 Skylark Drones -->
<template>
  <v-menu
    v-model="isOpen"
    offset-y
    nudge-bottom="6"
    :close-on-content-click="false"
    content-class="rounded-lg"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        text
        rounded
        class="ml-2 px-2"
        :class="buttonClasses"
        height="40"
        min-width="40"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon size="20">mdi-filter-outline</v-icon>
        <span v-if="hasActiveFilters" class="ml-1 text-body-2 font-weight-bold">
          {{ activeFilterCount }}
        </span>
      </v-btn>
    </template>
    <v-card
      :width="$vuetify.breakpoint.xs ? '90vw' : 550"
      class="pa-3 rounded-lg"
      elevation="2"
    >
      <div class="text-caption text-center pb-2">Filter by Support</div>
      <div class="d-flex flex-wrap align-center justify-center">
        <v-chip
          v-for="filter in filterOptions"
          :key="filter.key"
          pill
          class="ma-1"
          :outlined="!isFilterActive(filter.key)"
          :color="isFilterActive(filter.key) ? 'primary' : undefined"
          :text-color="isFilterActive(filter.key) ? 'white' : '#4a4a4b'"
          :close="isFilterActive(filter.key)"
          close-icon="mdi-close-circle"
          @click="toggleFilter(filter.key)"
          @click:close="toggleFilter(filter.key)"
        >
          {{ filter.label }}
        </v-chip>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { isEmpty } from 'lodash'

export default {
  name: 'SupportedDronesFilters',

  props: {
    drones: {
      type: Array,
      required: true
    },
    cameraModels: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      isOpen: false,
      activeFilters: [],
      filterOptions: [
        { key: 'manual_flying', label: 'Manual Flying' },
        { key: 'health_management', label: 'Health Management System' },
        { key: 'data_verification', label: 'Data Verification' },
        { key: 'rtk', label: 'RTK' },
        { key: 'live_stream', label: 'Live Stream' },
        { key: 'mission_execution', label: 'Mission Execution' },
        { key: 'mission_planning', label: 'Mission Planning' }
      ]
    }
  },

  computed: {
    hasActiveFilters() {
      return this.activeFilters.length > 0
    },
    activeFilterCount() {
      return this.activeFilters.length
    },
    buttonClasses() {
      if (this.isOpen) {
        return ['primary', 'white--text']
      }
      if (this.hasActiveFilters) {
        return ['orange', 'lighten-5', 'primary--text']
      }
      return ['grey', 'lighten-4', 'grey--text', 'text--darken-2']
    },
    cameraCapabilitiesByDroneId() {
      const capabilitiesByDroneId = {}

      if (!this.cameraModels || this.cameraModels.length === 0) {
        return capabilitiesByDroneId
      }

      this.cameraModels.forEach(model => {
        const compatibleDrones = Array.isArray(model.compatible_with)
          ? model.compatible_with
          : []

        compatibleDrones.forEach(droneId => {
          if (!capabilitiesByDroneId[droneId]) {
            capabilitiesByDroneId[droneId] = {
              missionExecution: false,
              missionPlanning: false,
              dataVerification: false
            }
          }

          Object.values(model.capabilities?.mission_types || {}).forEach(
            missionType => {
              if (missionType.mission_planning === 2) {
                capabilitiesByDroneId[droneId].missionPlanning = true
              }

              if (missionType.mission_execution === 2) {
                capabilitiesByDroneId[droneId].missionExecution = true
              }
            }
          )

          if (
            model.make &&
            model.sensor_attributes &&
            !isEmpty(model.sensor_attributes)
          ) {
            capabilitiesByDroneId[droneId].dataVerification = true
          }
        })
      })

      return capabilitiesByDroneId
    },
    filteredDrones() {
      if (!this.hasActiveFilters) {
        return this.drones
      }

      return this.drones.filter(drone => {
        return this.activeFilters.every(filterKey =>
          this.isDroneSupportingFilter(drone, filterKey)
        )
      })
    }
  },

  methods: {
    isFilterActive(filterKey) {
      return this.activeFilters.includes(filterKey)
    },
    toggleFilter(filterKey) {
      if (this.isFilterActive(filterKey)) {
        this.activeFilters = this.activeFilters.filter(
          activeFilter => activeFilter !== filterKey
        )
        return
      }
      this.activeFilters = [...this.activeFilters, filterKey]
    },
    isDroneSupportingFilter(drone, filterKey) {
      const baseCapabilities = {
        manual_flying: drone.capabilities?.free_flight === 2,
        health_management: drone.capabilities?.hms === 2,
        rtk: drone.capabilities?.ppk_rtk === 2,
        live_stream: drone.capabilities?.livestream === 2
      }

      const derivedCapabilities =
        this.cameraCapabilitiesByDroneId[drone.id] || {}

      const supportMap = {
        ...baseCapabilities,
        mission_execution: derivedCapabilities.missionExecution || false,
        mission_planning: derivedCapabilities.missionPlanning || false,
        data_verification: derivedCapabilities.dataVerification || false
      }

      return supportMap[filterKey]
    },
    emitFiltersChanged() {
      this.$emit('filters-changed', {
        filteredDrones: this.filteredDrones,
        hasActiveFilters: this.hasActiveFilters
      })
    }
  },

  watch: {
    isOpen(value) {
      if (value) {
        this.$emit('menu-opened')
      }
    },
    filteredDrones: {
      handler() {
        this.emitFiltersChanged()
      },
      immediate: true
    }
  }
}
</script>
